/* eslint-disable no-console */
import BackendProxy from '@logic/backendproxy';
import ContactFormFields from '@logic/contactform';
import MasterDataProvider from '@logic/masterdataprovider';
import { Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import {
  execValidateAndSaveContactAction,
  SAVE_CONTACT_FAIL,
  SAVE_CONTACT_OK,
} from '@src/actions/agentconsole';
import { useB2ChatToast } from '@src/components/Common/B2ChatToast';
import {
  SegmentedTab,
  SegmentedTabs,
} from '@src/components/Common/SegmentedTabs';
import { Component, createRef, useEffect, useRef } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import CustomField from '../customField';
import ContactFormViewer from './ContactFormViewer';
import {
  applyLinkedPhone,
  hasMissingRequiredCustomField,
} from './contactViewerUtils';
import './ContactViewer.css';
import { intlMessages, messages } from './contactViewerMessages';
import RequestPhoneNumberDialog from './RequestPhoneNumberDialog';

const a11yProps = index => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
});

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    style={{ overflowY: 'auto' }}
    {...other}
  >
    {value === index && <Box style={{ padding: '16px' }}>{children}</Box>}
  </div>
);

class ContactViewer extends Component {
  constructor(props) {
    super(props);
    this.customFieldRef = createRef();

    this.state = {
      arrSelCountryProvinces: [],
      contactDetail: null,
      customFields: [],
      customFieldsWithError: false,
      activeTab: 0,
      mobileNumberDisabled: null,
      customFieldsUpdated: false,
      showRequestPhoneDialog: false,
    };

    this.handleCityFieldChanged = this.handleCityFieldChanged.bind(this);
    this.handleSaveContact = this.handleSaveContact.bind(this);
    this.handleSetCountryProvinces = this.handleSetCountryProvinces.bind(this);
  }

  componentDidMount() {
    const { countriesDataSet, isLoggingOut, getGeneralProperties, contact } =
      this.props;

    if (countriesDataSet.length === 0 && !isLoggingOut) {
      getGeneralProperties();
    }

    if (contact?.id) {
      this.loadContactEditFormDetail(contact?.id);
    } else {
      this.setState({});
    }
  }

  componentDidUpdate(prevProps) {
    const { contact, contactInfo, notifications, contactForm, linkedPhone } =
      this.props;
    const { contactDetail } = this.state;

    // ENG-1304: patch only the phone, so the agent's unsaved edits survive
    const linked = applyLinkedPhone(
      contactDetail,
      linkedPhone,
      prevProps.linkedPhone
    );
    if (linked) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ contactDetail: linked });
      this.props.onChangeContactFormField(
        ContactFormFields.FIELDS.MOBILE_NUMBER,
        linked.mobileNumber
      );
    }

    const viaBotAccountChanged =
      this.props.viaBotAccountId !== prevProps.viaBotAccountId;

    if (
      contact &&
      (contact?.id !== prevProps?.contact?.id || viaBotAccountChanged)
    ) {
      this.loadContactEditFormDetail(contact.id);
    } else if (!contact && contactDetail) {
      // FIXME: probably this code is unnecessary
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ contactDetail: null, customFields: [], activeTab: 0 });
    }

    if (contactInfo && contactInfo !== prevProps.contactInfo) {
      this.processContactInfo(contactInfo);
    }

    if (
      notifications?.type === SAVE_CONTACT_OK &&
      prevProps.notifications?.type !== SAVE_CONTACT_OK &&
      contactDetail
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prev => ({
        contactDetail: {
          ...prev.contactDetail,
          mobileNumber:
            contactForm.fields[ContactFormFields.FIELDS.MOBILE_NUMBER].value,
        },
      }));
    }
  }

  onTabChange = (_event, index) => {
    this.setState({ activeTab: index });
  };

  loadContactEditFormDetail = contactId => {
    const { fetchContactById } = this.props;
    this.setState({
      customFieldsWithError: false,
      contactDetail: null,
      customFields: [],
      customFieldsUpdated: false,
      // The viewer is not remounted on chat switch: an open request-phone
      // dialog must not carry over to the next contact.
      showRequestPhoneDialog: false,
    });
    fetchContactById({
      id: String(contactId),
      viaBotAccount: this.props.viaBotAccountId,
    });
  };

  processContactInfo = contactData => {
    const { contact, citiesDataSet, loadContactForm, contactBotId } =
      this.props;

    loadContactForm(contactData, contact, citiesDataSet);

    BackendProxy.generalSetting(
      contactBotId,
      customFields => {
        const customFieldsWithError = hasMissingRequiredCustomField(
          customFields,
          contactData.attributes
        );

        this.setState({
          contactDetail: contactData,
          customFields,
          mobileNumberDisabled: null,
          customFieldsWithError,
        });
        this.getProvinces(contactData.country);
      },
      error => {
        console.error('Error getting custom fields', error);
      }
    );
  };

  errorFromCustomFields = withError => {
    this.setState({ customFieldsWithError: withError });
  };

  handleCityFieldChanged(fieldName, selVal, selValIx) {
    const { onChangeContactFormField } = this.props;
    if (selVal && selVal.value) {
      onChangeContactFormField(fieldName, selValIx);
    }
  }

  handleSaveContact() {
    const {
      contactBotId,
      contact,
      citiesDataSet,
      customForm,
      chatId,
      provider,
    } = this.props;

    execValidateAndSaveContactAction({
      chatId,
      provider,
      botId: contactBotId,
      contactId: contact.id,
      cities: citiesDataSet,
      fields: customForm,
    });
  }

  handleSetCountryProvinces(arrProvinces) {
    this.setState({
      arrSelCountryProvinces: arrProvinces,
    });
  }

  getProvinces(country) {
    if (country) {
      const { isoCode } = country;
      const { arrSelCountryProvinces } = this.state;

      if (arrSelCountryProvinces && arrSelCountryProvinces.length === 0) {
        MasterDataProvider.getCountryProvincesArray(isoCode, arrProvinces => {
          this.handleSetCountryProvinces(arrProvinces);
        });
      }
    } else {
      this.setState(state => ({
        ...state,
        arrSelCountryProvinces: [],
      }));
    }
  }

  updateCustomFields = () => {
    const { onChangeCustomContactFormField } = this.props;
    const { contactDetail, customFields } = this.state;

    const contactAttr = contactDetail && contactDetail.attributes;

    customFields.forEach(item => {
      if (item.activated) {
        if (contactAttr && contactAttr.length > 0) {
          // to set values to custom fields
          contactAttr.forEach((data, innerKey) => {
            if (item.id === data.attrId) {
              item.value = data.value;
              contactAttr.splice(innerKey, 1);
            }
          });
        }

        onChangeCustomContactFormField(item.id, item.value);
      }
    });
  };

  handleOnChangeCustomContactFormField = (attrId, value) => {
    const { onChangeCustomContactFormField } = this.props;
    const { customFields } = this.state;

    onChangeCustomContactFormField(attrId, value);

    const index = customFields.findIndex(item => `${item?.id}` === attrId);

    if (index !== -1) {
      this.setState({
        customFields: [
          ...customFields.slice(0, index),
          {
            ...customFields[index],
            value,
          },
          ...customFields.slice(index + 1),
        ],
      });
    }
  };

  render() {
    const {
      contact,
      contactForm,
      intl,
      countriesDataSet,
      onChangeContactFormField,
      viaBotAccountId,
    } = this.props;
    const {
      contactDetail,
      customFields,
      customFieldsWithError,
      mobileNumberDisabled,
      arrSelCountryProvinces,
      activeTab,
      customFieldsUpdated,
      showRequestPhoneDialog,
    } = this.state;

    if (customFields?.length && !customFieldsUpdated) {
      this.updateCustomFields();
      this.setState({
        customFieldsUpdated: true,
      });
    }

    const mobileNumberField =
      contactForm.fields[ContactFormFields.FIELDS.MOBILE_NUMBER];

    const ContactFormView = contact ? (
      <>
        <ContactFormViewer
          customFieldsWithError={customFieldsWithError}
          mobileNumberDisabled={mobileNumberDisabled}
          arrSelCountryProvinces={arrSelCountryProvinces}
          onChangeContactFormField={onChangeContactFormField}
          contactForm={contactForm}
          avatarUrl={contact.avatarUrl}
          countriesDataSet={countriesDataSet}
          handleSetCountryProvinces={this.handleSetCountryProvinces}
          bsuid={contactDetail?.waUserId}
          whatsappUsername={contactDetail?.waUsername}
          phoneRequestStatus={contactDetail?.waPhoneRequestStatus}
          hasWaUserId={!!contactDetail?.waUserId}
          savedMobileNumber={contactDetail?.mobileNumber}
          onRequestNumber={() =>
            this.setState({ showRequestPhoneDialog: true })
          }
          intl
        />
      </>
    ) : (
      <div className="NoActiveChatMsg">
        <FormattedMessage id="ContactForm.noActiveContact" />
      </div>
    );

    let disabled = false;
    let label = <FormattedMessage id="ContactForm.saveButton" />;

    if (
      contactForm.state &&
      contactForm.state === ContactFormFields.STATES.SAVING
    ) {
      disabled = true;
      label = <FormattedMessage id="ContactForm.saveButtonBusy" />;
    }

    const contactButton = contact && (
      <Button
        variant="contained"
        color="primary"
        onClick={this.handleSaveContact}
        disabled={disabled}
        style={{ marginBottom: 10, width: '237px' }}
      >
        {label}
      </Button>
    );

    // http code 409 is used for contact's mobile number validation. It can be used for others validation but some changes would be needed.
    if (contactForm.state === 409) {
      mobileNumberField.error = intl.formatMessage(
        messages.notUniqueMobileNumber
      );
    }

    const tabItems = [
      <SegmentedTab
        key={intl.formatMessage(intlMessages.standardFieldsTitle)}
        label={intl.formatMessage(intlMessages.standardFieldsTitle)}
        {...a11yProps(0)}
      />,
    ];
    const tabPanels = [
      <TabPanel value={activeTab} index={0} key="stdFields">
        {ContactFormView}
      </TabPanel>,
    ];

    if (customFields && customFields.length > 0) {
      const customFieldsView = (
        <CustomField
          onRef={this.customFieldRef}
          notifyError={this.errorFromCustomFields}
          editContactFlg
          contactData={contactDetail}
          customFields={customFields}
          onChangeCustomContactFormField={
            this.handleOnChangeCustomContactFormField
          }
        />
      );

      tabItems.push(
        <SegmentedTab
          key={intl.formatMessage(intlMessages.customFieldsTitle)}
          label={intl.formatMessage(intlMessages.customFieldsTitle)}
          {...a11yProps(1)}
        />
      );
      tabPanels.push(
        <TabPanel value={activeTab} index={1} key="customFields">
          {customFieldsView}
        </TabPanel>
      );
    }

    return (
      <div className="ContactViewer">
        <SegmentedTabs
          onChange={this.onTabChange}
          value={activeTab}
          indicatorColor="primary"
          style={{ margin: '0 16px' }}
        >
          {tabItems}
        </SegmentedTabs>
        {tabPanels}
        <div className="contactViewer-action">{contactButton}</div>
        {contactDetail?.waUserId && viaBotAccountId && (
          <RequestPhoneNumberDialog
            open={showRequestPhoneDialog}
            onClose={() => this.setState({ showRequestPhoneDialog: false })}
            onSuccess={status =>
              this.setState(prev => ({
                contactDetail: {
                  ...prev.contactDetail,
                  waPhoneRequestStatus: status,
                },
              }))
            }
            contactId={contact?.id}
            bsuid={contactDetail.waUserId}
            viaBotAccountId={viaBotAccountId}
          />
        )}
      </div>
    );
  }
}

const ContactViewerWithIntl = injectIntl(ContactViewer);

const ContactViewerWithToast = props => {
  const toast = useB2ChatToast();
  const intl = useIntl();
  const prevNotificationType = useRef(null);

  const { notifications } = props;

  useEffect(() => {
    if (
      notifications?.type &&
      notifications.type !== prevNotificationType.current
    ) {
      if (notifications.type === SAVE_CONTACT_OK) {
        toast.success(intl.formatMessage({ id: 'Contact.toast.editSuccess' }));
      } else if (notifications.type === SAVE_CONTACT_FAIL) {
        toast.error(intl.formatMessage({ id: 'Contact.toast.saveError' }));
      }
    }
    prevNotificationType.current = notifications?.type ?? null;
  }, [notifications?.type, toast, intl]);

  return <ContactViewerWithIntl {...props} />;
};

export default ContactViewerWithToast;
